// Pure utility functions extracted from pdfGenerator.js
// Kept separate so they can be unit-tested without importing jsPDF.

/** Strip control characters so jsPDF does not corrupt layout */
export function san(val) {
  if (val == null) return '—';
  return String(val).replace(/[\r\n\t\0]/g, ' ').trim() || '—';
}

/**
 * Sanitise a string for jsPDF WinAnsiEncoding (latin-1 subset).
 * jsPDF standard fonts only cover characters up to U+00FF.
 * Any character outside that range causes jsPDF to switch the ENTIRE
 * string to wide-character mode, spacing every character out.
 * Replaces common pharma/science Unicode symbols with ASCII equivalents.
 */
export function sanPdf(val) {
  if (val == null) return '—';
  return String(val)
    .replace(/[\r\n\t\0]/g, ' ')
    // Comparison / math symbols
    .replace(/\u2265/g, '>=')   // ≥
    .replace(/\u2264/g, '<=')   // ≤
    .replace(/\u2260/g, '!=')   // ≠
    .replace(/\u00B1/g, '+/-')  // ±
    .replace(/\u00D7/g, 'x')    // ×
    .replace(/\u00F7/g, '/')    // ÷
    .replace(/\u2248/g, '~')    // ≈
    // Greek letters common in pharma specs
    .replace(/[\u03BC\u00B5]/g, 'u')  // μ / µ (micro)
    .replace(/\u03B1/g, 'alpha')
    .replace(/\u03B2/g, 'beta')
    .replace(/\u03B3/g, 'gamma')
    .replace(/\u03B4/g, 'delta')
    .replace(/\u03B5/g, 'epsilon')
    // Degree / punctuation
    .replace(/\u00B0/g, 'deg')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/\u2013/g, '-')
    .replace(/\u2014/g, '--')
    .replace(/\u2026/g, '...')
    // Strip anything else outside latin-1
    .replace(/[^\x00-\xFF]/g, '?')
    .trim() || '—';
}

/** Format a date value as DD-MM-YYYY */
export function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt)) return String(d);
  return `${String(dt.getDate()).padStart(2,'0')}-${String(dt.getMonth()+1).padStart(2,'0')}-${dt.getFullYear()}`;
}

/** Formats an address by joining parts while avoiding duplicates */
export function formatAddr(main, city, state, zip, country) {
  if (!main) {
    const sub = [city, state, zip].filter(Boolean).join(', ');
    return [sub, country].filter(Boolean).join('\n');
  }
  const mainClean = String(main).trim();
  const cityClean = String(city || '').trim();
  const countryClean = String(country || '').trim();
  const result = [mainClean];
  const subLine = [city, state, zip].filter(Boolean).join(', ');
  if (subLine && !mainClean.toLowerCase().includes(cityClean.toLowerCase())) {
     result.push(subLine);
  }
  if (countryClean && !mainClean.toLowerCase().includes(countryClean.toLowerCase())) {
     result.push(countryClean);
  }
  return result.join('\n');
}

/** Convert a numeric amount to Indian-English words */
export function numberToWords(amount) {
  if (!amount || isNaN(amount)) return 'ZERO';
  const ones = ['','ONE','TWO','THREE','FOUR','FIVE','SIX','SEVEN','EIGHT','NINE',
                 'TEN','ELEVEN','TWELVE','THIRTEEN','FOURTEEN','FIFTEEN','SIXTEEN',
                 'SEVENTEEN','EIGHTEEN','NINETEEN'];
  const tens = ['','','TWENTY','THIRTY','FORTY','FIFTY','SIXTY','SEVENTY','EIGHTY','NINETY'];
  const two   = n => n < 20 ? ones[n] : tens[Math.floor(n/10)] + (n%10 ? ' '+ones[n%10] : '');
  const three = n => !n ? '' : n < 100 ? two(n) : ones[Math.floor(n/100)]+' HUNDRED'+(n%100?' '+two(n%100):'');
  const num = Math.round(amount * 100) / 100;
  const int = Math.floor(num);
  const dec = Math.round((num - int) * 100);
  let w = '';
  if (!int) { w = 'ZERO'; } else {
    const cr = Math.floor(int / 10000000);
    const lk = Math.floor((int % 10000000) / 100000);
    const th = Math.floor((int % 100000) / 1000);
    const re = int % 1000;
    if (cr) w += three(cr)+' CRORE ';
    if (lk) w += three(lk)+' LAKH ';
    if (th) w += three(th)+' THOUSAND ';
    if (re) w += three(re);
  }
  w = w.trim();
  if (dec > 0) w += ` AND ${two(dec)} PAISE`;
  return w;
}

/** Return the English unit name for a currency code */
export function currencyUnitName(currency) {
  const cur = (currency || 'USD').toString().trim().toUpperCase();
  switch (cur) {
    case 'INR': return 'RUPEES';
    case 'USD': return 'DOLLARS';
    case 'EUR': return 'EUROS';
    case 'GBP': return 'POUNDS';
    case 'AUD': return 'DOLLARS';
    case 'CAD': return 'DOLLARS';
    default: return '';
  }
}

/** Format amount-in-words line for PDF footer */
export function formatAmountInWords(cur, amount) {
  const unit = currencyUnitName(cur);
  return unit
    ? `Amount: ${numberToWords(amount)} ${unit} ONLY`
    : `Amount: ${numberToWords(amount)} ONLY`;
}

/**
 * Derive GST tax info from an order record.
 * Returns { isExport, isIGST, cgst, sgst, igst }
 */
export function getTaxInfo(order) {
  const cur = (order.currency || 'USD').toUpperCase();
  const taxType = order.tax_type || 'none';
  const taxRate = Number(order.tax_rate) || 0;
  if (taxType === 'export' || cur !== 'INR') {
    return { isExport: true, isIGST: true, cgst: 0, sgst: 0, igst: 0 };
  }
  if (taxType === 'none') {
    return { isExport: false, isIGST: false, cgst: 0, sgst: 0, igst: 0 };
  }
  if (taxType === 'cgst_sgst') {
    const half = taxRate / 2;
    return { isExport: false, isIGST: false, cgst: half, sgst: half, igst: 0 };
  }
  return { isExport: false, isIGST: true, cgst: 0, sgst: 0, igst: taxRate };
}

/** Effective tax rate for a line item (item override or order-level fallback) */
export function getItemTaxRate(it, orderTax) {
  return it.item_tax_rate != null
    ? Number(it.item_tax_rate)
    : (orderTax.isIGST ? orderTax.igst : (orderTax.cgst + orderTax.sgst));
}

/** Compute all totals needed for PDF rendering */
export function computeTotals(order) {
  const tax        = getTaxInfo(order);
  const netAmt     = Number(order.subtotal || 0) - Number(order.discount_amount || 0);
  const freightAmt = Number(order.freight_amount  || 0);
  const freightGst = freightAmt * Number(order.freight_gst_pct || 0) / 100;
  const charges    = Array.isArray(order.charges) && order.charges.length > 0
    ? order.charges
    : (Number(order.other_charges) > 0 ? [{ label: 'Other Charges', amount: Number(order.other_charges), gst_pct: 0 }] : []);
  const chargesAmt    = charges.reduce((s, c) => s + (Number(c.amount) || 0), 0);
  const chargesGstAmt = charges.reduce((s, c) => s + (Number(c.amount) || 0) * (Number(c.gst_pct) || 0) / 100, 0);
  let itemTax = 0;
  (order.items || []).forEach(it => {
    const a = Number(it.total || 0);
    if (tax.isExport) return;
    const r = getItemTaxRate(it, tax);
    itemTax += a * r / 100;
  });
  const netTax  = itemTax + freightGst;
  const gross   = netAmt + netTax + freightAmt + chargesAmt + chargesGstAmt;
  const rounding = Math.round(gross) - gross;
  const payable  = Math.round(gross);
  return { tax, netAmt, freightAmt, freightGst, charges, chargesAmt, chargesGstAmt, itemTax, netTax, gross, rounding, payable };
}
